import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useOverviewDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.store';
import { useOverviewKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useOverviewKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { ShiftBreakdownChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/ShiftBreakdownChart';
import { FulfillmentTrendChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/FulfillmentTrendChart';
import { FulfillmentByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/FulfillmentByPositionChart';
import { CallOutByLocationChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/CallOutByLocationChart';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  WidgetGrid,
  TallWidgetCell,
  FullWidthSection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { TrendGranularity } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.types';

const TREND_VIEW_SUBTITLES: Record<TrendGranularity, string> = {
  daily: 'Daily view',
  weekly: 'Weekly view',
  monthly: 'Monthly view',
};

export const OverviewDashboard: React.FC = () => {
  const fetchData = useOverviewDashboardStore((s) => s.fetchData);
  const reset = useOverviewDashboardStore((s) => s.reset);
  const isLoading = useOverviewDashboardStore((s) => s.isLoading);
  const trendGranularity = useOverviewDashboardStore((s) => s.trendGranularity);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const kpiCards = useOverviewKpiCards();
  const showSkeleton = isLoading;

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label="Overview Dashboard">
      <DashboardHeader>
        <DashboardTitle>Overview</DashboardTitle>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {showSkeleton ? (
          <DashboardSkeleton kpiCount={4} widgetCount={3} />
        ) : (
          <Fragment>
            <div aria-live="polite">
              <KpiStatCardRow cards={kpiCards} />
            </div>
            <WidgetGrid>
              <TallWidgetCell>
                <WidgetCardWrapper
                  title="Shift breakdown"
                  subtitle="Distribution of shifts by outcome"
                >
                  <ShiftBreakdownChart />
                </WidgetCardWrapper>
              </TallWidgetCell>
              <WidgetCardWrapper
                title="Fulfillment by position"
                subtitle="Fulfillment percentage per position"
              >
                <FulfillmentByPositionChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Call out volume by location"
                subtitle="Total call out shifts per location"
              >
                <CallOutByLocationChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Trend — Total Shifts & Fulfillment Percentage"
                subtitle={trendGranularity ? TREND_VIEW_SUBTITLES[trendGranularity] : undefined}
              >
                <FulfillmentTrendChart />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
